import type { LibraryNavigationRequest, LibraryOpenMode, LibrarySection } from '../types/app';

const SECTIONS: LibrarySection[] = ['own', 'my_shares', 'shared_public', 'shared_private'];
const MODES: LibraryOpenMode[] = ['replay', 'play', 'poll'];

function parseSection(value: string | null): LibrarySection | undefined {
  if (value && SECTIONS.includes(value as LibrarySection)) {
    return value as LibrarySection;
  }
  return undefined;
}

function parseMode(value: string | null): LibraryOpenMode | undefined {
  if (value && MODES.includes(value as LibraryOpenMode)) {
    return value as LibraryOpenMode;
  }
  return undefined;
}

function parsePositiveInt(value: string | null): number | undefined {
  if (!value) return undefined;
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/** Lit les paramètres bibliothèque depuis une query string ou l'URL courante. */
export function parseLibraryDeepLink(search?: string): LibraryNavigationRequest | null {
  const raw = search ?? (typeof window !== 'undefined' ? window.location.search : '');
  const params = new URLSearchParams(raw.startsWith('?') ? raw.slice(1) : raw);

  const section = parseSection(params.get('library'));
  const sharedGameId = parsePositiveInt(params.get('libraryGame'));
  const mode = parseMode(params.get('libraryMode'));
  const pendingPollsOnly = params.get('libraryPolls') === '1';
  const highlightOnly = params.get('libraryHighlight') === '1';

  if (!section && !sharedGameId && !pendingPollsOnly) {
    return null;
  }

  return {
    ...(section ? { section } : {}),
    ...(sharedGameId ? { sharedGameId } : {}),
    ...(mode ? { mode } : {}),
    ...(pendingPollsOnly ? { pendingPollsOnly } : {}),
    ...(highlightOnly ? { highlightOnly } : {}),
  };
}

/** Met à jour l'URL (replaceState) avec l'état bibliothèque courant. */
export function syncLibraryUrl(update: {
  section?: LibrarySection;
  sharedGameId?: number | null;
  mode?: LibraryOpenMode | null;
  pendingPollsOnly?: boolean;
  highlightOnly?: boolean;
}): void {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);

  if (update.section !== undefined) {
    url.searchParams.set('library', update.section);
  }

  if (update.sharedGameId === null) {
    url.searchParams.delete('libraryGame');
    url.searchParams.delete('libraryMode');
    url.searchParams.delete('libraryHighlight');
  } else if (update.sharedGameId !== undefined) {
    url.searchParams.set('libraryGame', String(update.sharedGameId));
  }

  if (update.mode === null) {
    url.searchParams.delete('libraryMode');
  } else if (update.mode !== undefined) {
    url.searchParams.set('libraryMode', update.mode);
  }

  if (update.pendingPollsOnly !== undefined) {
    if (update.pendingPollsOnly) url.searchParams.set('libraryPolls', '1');
    else url.searchParams.delete('libraryPolls');
  }

  if (update.highlightOnly !== undefined) {
    if (update.highlightOnly) url.searchParams.set('libraryHighlight', '1');
    else url.searchParams.delete('libraryHighlight');
  }

  const next = `${url.pathname}${url.search}${url.hash}`;
  if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
    window.history.replaceState(window.history.state, '', next);
  }
}

/** Applique une navigation bibliothèque à l'URL. */
export function writeLibraryDeepLink(request: LibraryNavigationRequest): void {
  syncLibraryUrl({
    section: request.section ?? 'shared_private',
    sharedGameId: request.sharedGameId ?? null,
    mode: request.sharedGameId && !request.highlightOnly ? (request.mode ?? 'replay') : null,
    pendingPollsOnly: request.pendingPollsOnly ?? false,
    highlightOnly: request.highlightOnly ?? false,
  });
}

/** Retire les paramètres bibliothèque de l'URL. */
export function clearLibraryUrlParams(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('library');
  url.searchParams.delete('libraryGame');
  url.searchParams.delete('libraryMode');
  url.searchParams.delete('libraryPolls');
  url.searchParams.delete('libraryHighlight');
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, '', next);
}

/** Construit un lien partageable vers une partie partagée. */
export function buildLibraryShareUrl(
  sharedGameId: number,
  options?: {
    section?: LibrarySection;
    mode?: LibraryOpenMode;
    highlightOnly?: boolean;
  },
): string {
  if (typeof window === 'undefined') return '';
  const url = new URL(window.location.origin + window.location.pathname);
  url.searchParams.set('library', options?.section ?? 'shared_private');
  url.searchParams.set('libraryGame', String(sharedGameId));
  if (options?.highlightOnly) {
    url.searchParams.set('libraryHighlight', '1');
  } else if (options?.mode) {
    url.searchParams.set('libraryMode', options.mode);
  }
  return url.toString();
}
